import { useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCustomers } from "../../store/billingManagement/billingManagementSlice";

/** Large enough that the MSP customer list usually includes the org in one request after refresh. */
const BILLING_CUSTOMERS_PAGE_SIZE_FOR_ORG_LABEL = 500;

/**
 * Resolves customer org display name for `/billing-management/customer/:customerOrgCode/...` routes.
 * After a full reload, Redux `customers` is often empty; this dispatches `getCustomers` once until the
 * org appears in the list (or the fetch completes without it).
 */
export function useBillingCustomerOrgDisplay(customerOrgCode, options = {}) {
  const { fallbackName = "", enabled = true } = options;
  const dispatch = useDispatch();
  const customers = useSelector((state) => state.billingManagement.customers);
  const isFetchingCustomers = useSelector((state) => state.billingManagement.isFetchingCustomers);

  const customer = useMemo(() => {
    if (!customerOrgCode || !Array.isArray(customers) || customers.length === 0) return null;
    return customers.find((c) => c?.org_code === customerOrgCode) ?? null;
  }, [customers, customerOrgCode]);

  const fetchAttemptedRef = useRef(null);

  useEffect(() => {
    fetchAttemptedRef.current = null;
  }, [customerOrgCode]);

  useEffect(() => {
    if (!enabled || !customerOrgCode) return;
    if (customer) return;
    if (isFetchingCustomers) return;
    if (fetchAttemptedRef.current === customerOrgCode) return;
    fetchAttemptedRef.current = customerOrgCode;
    dispatch(
      getCustomers({
        page: 1,
        page_size: BILLING_CUSTOMERS_PAGE_SIZE_FOR_ORG_LABEL,
      })
    );
  }, [customerOrgCode, customer, dispatch, enabled, isFetchingCustomers]);

  const displayName = useMemo(() => {
    if (customer?.org_name) return customer.org_name;
    if (fallbackName) return fallbackName;
    return customerOrgCode || "";
  }, [customer, fallbackName, customerOrgCode]);

  return {
    displayName,
    customer,
    isResolvingOrgLabel: Boolean(enabled && customerOrgCode && !customer && isFetchingCustomers),
  };
}
